import { clearStored, readStored, writeStored } from './storage.js';

const EXPOSURE_KEY = 'exp';

// Written through on every record, so a blocked store still dedupes within the page load.
let exposed: Record<string, string> | undefined;

function parse(raw: string | null): Record<string, string> {
  if (!raw) {
    return {};
  }
  try {
    const value: unknown = JSON.parse(raw);
    if (typeof value !== 'object' || value === null || Array.isArray(value)) {
      return {};
    }
    return Object.fromEntries(Object.entries(value).filter(([, variant]) => typeof variant === 'string')) as Record<string, string>;
  } catch {
    return {};
  }
}

/** Rides along on every event, so a conversion carries its variant without the caller passing one. */
export function getExposedExperiments(): Record<string, string> {
  exposed ??= parse(readStored(EXPOSURE_KEY));
  return exposed;
}

/** False when already recorded, i.e. the caller must not emit again. Durable, so it survives a reload. */
export function recordExposure(experiment: string, variant: string): boolean {
  // Another tab may have recorded since this page load; writing the page-load cache back would drop
  // its entry and re-emit that exposure after a reload. Store wins, so a blocked store still dedupes.
  const current = { ...exposed, ...parse(readStored(EXPOSURE_KEY)) };
  if (experiment in current) {
    exposed = current;
    return false;
  }
  exposed = { ...current, [experiment]: variant };
  writeStored(EXPOSURE_KEY, JSON.stringify(exposed));
  return true;
}

/** @internal: test-only, drops the cache but keeps storage, i.e. what a reload does. */
export function _forgetExperimentsCache() {
  exposed = undefined;
}

/** @internal: test-only reset */
export function _resetExperiments() {
  _forgetExperimentsCache();
  clearStored(EXPOSURE_KEY);
}
